import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSearch } from "@fortawesome/free-solid-svg-icons";
import Filters from "./newfilters";
import ExportDropdown from "./ExportDropdown";
import LoadingSpinner from "../LoadingSpinner";
import CircularProgressBar from "../CircularProgressBar";

const MOBILE_SORT_OPTIONS = [
  { label: "Position Rank", value: "rank" },
  { label: "Position Score", value: "positionScore" },
  { label: "Trackit Score", value: "trackittScore" },
];

const hasMeasurementParts = (feet, inches) =>
  [feet, inches].some(
    (part) => part !== null && part !== undefined && part !== "",
  );

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getMobileAthleteKey = (athlete, index) =>
  athlete?.userId || athlete?.athleteId || `${athlete?.athleteName}-${index}`;

const formatMobileMeasurement = (feet, inches, fallback) => {
  if (hasMeasurementParts(feet, inches)) {
    return `${toSafeNumber(feet) || 0}' ${toSafeNumber(inches) || 0}"`;
  }

  const totalInches = toSafeNumber(fallback);
  if (totalInches !== null) {
    const formattedFeet = Math.floor(totalInches / 12);
    const formattedInches = Number((totalInches % 12).toFixed(2));
    return `${formattedFeet}' ${formattedInches}"`;
  }

  return fallback || "";
};

const formatMobileRank = (rank) => {
  const parsed = Number(rank);
  if (Number.isFinite(parsed)) return String(Math.round(parsed));
  return "";
};

const getMobilePositionRank = (athlete) => {
  const positionAbbreviation = athlete?.primaryPositionAbbreviation || "";
  const rank = formatMobileRank(athlete?.rank);

  return [positionAbbreviation, rank ? `#${rank}` : ""]
    .filter(Boolean)
    .join(" ");
};

const formatMobileRounded = (value) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return Math.round(parsed);
  return value || "";
};

const formatMobileValue = (value, suffix = "") =>
  value === null || value === undefined || value === ""
    ? ""
    : `${value}${suffix}`;

const formatMobileBenchPress = (value) =>
  value === null || value === undefined || value === ""
    ? ""
    : String(value).replace(/\s*x\s*/i, " \u00d7 ");

const MOBILE_BASE_METRIC_COLUMNS = [
  "offerTierName",
  "trackittScore",
  "height",
  "weight",
  "wingspan",
  "hand",
];

const MOBILE_DEFAULT_FILTER_COLUMNS = [
  "fortyYardDash",
  "tenYardDash",
  "shuttle",
  "benchPress",
  "verticalJump",
  "broadJump",
  "threeCone",
];

const MOBILE_METRIC_LABELS = {
  offerTierName: "Offer Tier",
  trackittScore: "Trackit Score",
  height: "Height",
  weight: "Weight",
  wingspan: "Wingspan",
  hand: "Hand",
  fortyYardDash: "40 Yard Dash",
  shuttle: "20 Yard Shuttle",
  tenYardDash: "10 Yard Split",
  twentyYardDash: "20 Yard Split",
  benchPress: "Bench Press",
  verticalJump: "Vertical Jump",
  broadJump: "Broad Jump",
  threeCone: "Three Cone L-drill",
};

const getMobileMetricValue = (athlete, column) => {
  switch (column) {
    case "offerTierName":
      return formatMobileValue(athlete?.offerTierName);
    case "rank":
      return getMobilePositionRank(athlete);
    case "positionScore":
      return formatMobileRounded(athlete?.positionScore);
    case "trackittScore":
      return formatMobileRounded(athlete?.trackittScore);
    case "height":
      return formatMobileMeasurement(
        athlete?.heightFt,
        athlete?.heightIn,
        athlete?.height,
      );
    case "weight":
      return formatMobileValue(athlete?.weight, " lbs");
    case "wingspan":
      return formatMobileMeasurement(
        athlete?.wingSpanFt,
        athlete?.wingSpanIn,
        athlete?.wingspan,
      );
    case "hand":
      return formatMobileValue(athlete?.hand, "''");
    case "benchPress":
      return formatMobileBenchPress(athlete?.benchPress);
    default:
      return formatMobileValue(athlete?.[column]);
  }
};

const getMobileMetricColumns = (activeFilter, columnFilters) => {
  const selectedFilter = columnFilters.find(
    (filter) => filter.label === activeFilter,
  );
  const filterColumns = selectedFilter?.columns?.length
    ? selectedFilter.columns
    : MOBILE_DEFAULT_FILTER_COLUMNS;

  return [...new Set([...MOBILE_BASE_METRIC_COLUMNS, ...filterColumns])].filter(
    (column) => MOBILE_METRIC_LABELS[column],
  );
};

const getMobileMetricRows = (athlete, activeFilter, columnFilters) => {
  const metrics = getMobileMetricColumns(activeFilter, columnFilters).map(
    (column) => ({
      label: MOBILE_METRIC_LABELS[column],
      value: getMobileMetricValue(athlete, column),
    }),
  );

  return metrics.reduce((rows, metric, index) => {
    if (index % 2 === 0) rows.push([metric]);
    else rows[rows.length - 1].push(metric);
    return rows;
  }, []);
};

export default function MobileLeaderboardView({
  activeFilter,
  canExportContactInfo,
  columnFilters,
  disabledExport,
  filterParams,
  handleViewClick,
  includeGraduatedKids,
  isExportingContactInfo,
  isHighSchoolCoach,
  isHighSchoolLeaderboard,
  loading,
  onApplyFilters,
  onClearFilters,
  onExport,
  onExportContactInfo,
  search,
  setActiveFilter,
  setIncludeGraduatedKids,
  setSearch,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
  sortedFilteredAthletes,
}) {
  const mobileSortRef = useRef(null);
  const [mobileSortDraft, setMobileSortDraft] = useState({
    sortBy: "rank",
    sortDirection: "asc",
  });
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);
  const [mobileMetricsVisible, setMobileMetricsVisible] = useState(false);
  const [expandedMobileRows, setExpandedMobileRows] = useState(new Set());

  const mobileAthleteRowKeys = useMemo(
    () =>
      (sortedFilteredAthletes || []).map((athlete, index) =>
        getMobileAthleteKey(athlete, index),
      ),
    [sortedFilteredAthletes],
  );

  useEffect(() => {
    const handleMobileSortClickOutside = (event) => {
      if (
        mobileSortRef.current &&
        !mobileSortRef.current.contains(event.target)
      ) {
        setIsMobileSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMobileSortClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleMobileSortClickOutside);
  }, []);

  useEffect(() => {
    setMobileMetricsVisible(expandedMobileRows.size > 0);
  }, [expandedMobileRows]);

  const setAllMobileMetrics = (visible) => {
    setMobileMetricsVisible(visible);
    setExpandedMobileRows(visible ? new Set(mobileAthleteRowKeys) : new Set());
  };

  const toggleMobileAthleteRow = (rowKey) => {
    setExpandedMobileRows((previousRows) => {
      const nextRows = new Set(previousRows);

      if (nextRows.has(rowKey)) {
        nextRows.delete(rowKey);
      } else {
        nextRows.add(rowKey);
      }

      return nextRows;
    });
  };

  const openMobileSort = () => {
    setMobileSortDraft({ sortBy, sortDirection });
    setIsMobileSortOpen((isOpen) => !isOpen);
  };

  const applyMobileSort = () => {
    setSortBy(mobileSortDraft.sortBy);
    setSortDirection(mobileSortDraft.sortDirection);
    setIsMobileSortOpen(false);
  };

  const getMobileMetaParts = (athlete) =>
    [
      athlete?.graduation,
      athlete?.primaryPosition,
      athlete?.highSchool,
      athlete?.state,
    ].filter((part) => part !== null && part !== undefined && part !== "");

  const renderAthleteCard = (athlete, index) => {
    const rowKey = getMobileAthleteKey(athlete, index);
    const isExpanded = expandedMobileRows.has(rowKey);
    const metaParts = getMobileMetaParts(athlete);
    const positionRank = getMobilePositionRank(athlete);
    const positionScore = toSafeNumber(athlete?.positionScore) ?? 0;

    return (
      <div
        key={rowKey}
        className="rounded-md border border-gray-300 bg-white px-3 py-3 text-gray-600"
      >
        <div className="flex items-center gap-2">
          <div className="w-12 shrink-0 text-sm font-semibold leading-tight text-black">
            {positionRank}
          </div>
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => handleViewClick(athlete.userId)}
              className="block max-w-full truncate text-left text-base font-semibold leading-tight text-black"
            >
              {athlete.athleteName}
            </button>
            <div className="mt-1 truncate text-sm font-medium">
              {metaParts.join(" \u2022 ")}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <CircularProgressBar
              progress={positionScore}
              size={48}
              strokeWidth={4}
              color="#1C4ED8"
              backgroundColor="#E5ECFF"
              ariaLabel={`Position score: ${positionScore}`}
            />
            <button
              type="button"
              aria-label={isExpanded ? "Collapse athlete" : "Expand athlete"}
              onClick={() => toggleMobileAthleteRow(rowKey)}
              className="flex h-5 w-5 items-center justify-center text-gray-600"
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-sm transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="mt-3 border-t border-gray-300 pt-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm leading-6">
              {getMobileMetricRows(athlete, activeFilter, columnFilters).map(
                (row) =>
                  row.map((metric, metricIndex) => (
                    <div
                      key={`${metric.label}-${metricIndex}`}
                      className={metricIndex === 1 ? "text-right" : ""}
                    >
                      <span>{metric.label}: </span>
                      <span className="font-medium text-black">
                        {metric.value}
                      </span>
                    </div>
                  )),
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <h1 className="min-w-0 flex-1 text-xl font-semibold text-black">
            Leaderboard
          </h1>
          <div className="flex shrink-0 items-start gap-2 sm:gap-3">
            <div className="[&>button]:h-10 [&>button]:rounded-md [&>button]:border [&>button]:border-[#d1d7e0] [&>button]:bg-[#e8ebf1] [&>button]:px-3 [&>button]:text-base sm:[&>button]:h-[56px] sm:[&>button]:min-w-[178px] sm:[&>button]:px-6 [&>div>button]:h-10 [&>div>button]:rounded-md [&>div>button]:border [&>div>button]:border-[#d1d7e0] [&>div>button]:bg-[#e8ebf1] [&>div>button]:px-3 [&>div>button]:text-base sm:[&>div>button]:h-[56px] sm:[&>div>button]:min-w-[178px] sm:[&>div>button]:px-6">
              <ExportDropdown
                canExportContactInfo={canExportContactInfo}
                disabled={disabledExport}
                isExportingContactInfo={isExportingContactInfo}
                onExport={onExport}
                onExportContactInfo={onExportContactInfo}
              />
            </div>
            <div className="[&>div>button]:mb-0 [&>div>button]:h-10 [&>div>button]:rounded-md [&>div>button]:border [&>div>button]:border-[#d1d7e0] [&>div>button]:bg-[#e8ebf1] [&>div>button]:px-3 [&>div>button]:text-base sm:[&>div>button]:h-[56px] sm:[&>div>button]:min-w-[138px] sm:[&>div>button]:px-6">
              <Filters
                onApplyFilters={onApplyFilters}
                onClearFilters={onClearFilters}
                filterParams={filterParams}
                includeGraduatedKids={includeGraduatedKids}
                setIncludeGraduatedKids={setIncludeGraduatedKids}
                isHighSchoolLeaderboard={isHighSchoolLeaderboard}
                isHighSchoolCoach={isHighSchoolCoach}
              />
            </div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-[minmax(0,1fr)_auto] gap-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-gray-400"
            />
            <input
              type="text"
              placeholder="Search player name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-md border border-[#d5dce5] bg-white pl-12 pr-3 text-base text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 sm:text-xl"
            />
          </div>
          <button
            onClick={onClearFilters}
            className="h-10 rounded-md border border-blue-500 bg-white px-3 text-sm font-normal text-blue-600 sm:px-4 sm:text-xl"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      <div className="mt-6">
        <span className="text-sm font-semibold text-black sm:text-xl">
          Columns Filters:
        </span>
        <div className="-mx-1 mt-3 overflow-x-auto pb-1">
          <div className="flex min-w-max items-center gap-3 px-1">
            {columnFilters.map((item) => {
              const isActive = item.label === activeFilter;

              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setActiveFilter(item.label)}
                  className={`flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-base sm:text-xl ${
                    isActive
                      ? "border-gray-300 bg-blue-200 text-blue-600"
                      : "border-gray-300 bg-white text-gray-600"
                  }`}
                >
                  <img
                    src={item.image}
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6 object-contain"
                  />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="relative mt-6 flex items-center justify-between gap-3"
        ref={mobileSortRef}
      >
        <button
          type="button"
          onClick={openMobileSort}
          className="flex h-10 w-[118px] shrink-0 items-center justify-center gap-3 rounded-md border border-gray-300 bg-white text-base text-black sm:w-[180px]"
        >
          Sort By
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`text-gray-600 transition-transform text-sm ${
              isMobileSortOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <span className="text-base text-black sm:text-xl">Metrics:</span>
          <div className="inline-flex h-10 overflow-hidden rounded-md border border-blue-500 text-sm ">
            <button
              type="button"
              onClick={() => setAllMobileMetrics(false)}
              className={`w-[74px] sm:w-[96px] ${
                !mobileMetricsVisible
                  ? "bg-blue-200 text-blue-600"
                  : "bg-white text-gray-600"
              }`}
            >
              Hide
            </button>
            <button
              type="button"
              onClick={() => setAllMobileMetrics(true)}
              className={`w-[74px] border-l border-blue-500 sm:w-[104px] ${
                mobileMetricsVisible
                  ? "bg-blue-200 text-blue-600"
                  : "bg-white text-gray-600"
              }`}
            >
              Show
            </button>
          </div>
        </div>
        {isMobileSortOpen && (
          <div className="absolute left-0 top-[48px] z-30 w-[374px] max-w-[calc(100vw-32px)] overflow-hidden rounded-[10px] border border-gray-300 bg-white shadow-lg">
            <div className="p-5">
              <div className="mb-4 text-base font-semibold text-gray-600">
                Choose value
              </div>
              <div className="space-y-4">
                {MOBILE_SORT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-4 text-base ${
                      mobileSortDraft.sortBy === option.value
                        ? "text-black"
                        : "text-gray-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="mobileSortBy"
                      value={option.value}
                      checked={mobileSortDraft.sortBy === option.value}
                      onChange={() =>
                        setMobileSortDraft((draft) => ({
                          ...draft,
                          sortBy: option.value,
                        }))
                      }
                      className="h-5 w-5 accent-red-500"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <div className="mb-5 mt-7 text-base font-semibold text-gray-600">
                Order
              </div>
              <div className="space-y-4 pb-4">
                {[
                  { label: "Ascending (Lowest)", value: "asc" },
                  { label: "Descending (Highest)", value: "desc" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-4 text-base ${
                      mobileSortDraft.sortDirection === option.value
                        ? "text-black"
                        : "text-gray-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="mobileSortDirection"
                      value={option.value}
                      checked={mobileSortDraft.sortDirection === option.value}
                      onChange={() =>
                        setMobileSortDraft((draft) => ({
                          ...draft,
                          sortDirection: option.value,
                        }))
                      }
                      className="h-5 w-5 accent-red-500"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-300 px-8 py-4 text-center">
              <button
                type="button"
                onClick={applyMobileSort}
                className="px-8 py-3 rounded-md bg-red-500 text-base font-medium text-white"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="rounded-[10px] border border-gray-300 bg-white p-6">
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          </div>
        ) : sortedFilteredAthletes.length === 0 ? (
          <div className="rounded-[10px] border border-gray-300 bg-white p-6 text-center">
            <div className="flex flex-col items-center justify-center text-gray-600">
              <img
                className="mb-4 h-12"
                alt="No Result Found"
                src="/no-data.png"
              />
              <h1 className="mb-1 text-xl font-semibold text-gray-800">
                No Result Found
              </h1>
              <p className="text-base text-gray-600">
                Try adjusting the filters to find what you're looking for.
              </p>
            </div>
          </div>
        ) : (
          sortedFilteredAthletes.map((athlete, index) =>
            renderAthleteCard(athlete, index),
          )
        )}
      </div>
    </>
  );
}
